/**
 * 키에 속한 로컬 상태 — "키 하나 = 상태 하나". 다이얼로그 열림·선택 대상처럼 화면의 키(프로젝트·모델·인스턴스)에 속한
 * 상태가 제자리 라우트 전환에 살아남으면, 옛 키의 상태로 새 키에 명령이 나간다(P 에서 연 삭제 확인이 Q 의 모델을 지운다,
 * #390 R10). 키가 바뀌면 **렌더 중에** 그 키의 initial 을 커밋한다(가드된 파생 상태) — 효과 순서에 기대는 리셋이 없고,
 * 재방문(P→Q→P)에 옛 값이 되살아나지 않는다(R11).
 */
import { useCallback, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

export function useKeyedState<T>(key: string | null | undefined, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const [stored, setStored] = useState<{ key: string | null | undefined; value: T }>({ key, value: initial });
  const initialRef = useRef(initial);
  initialRef.current = initial;
  // 키가 바뀐 렌더: 이 키의 initial 을 즉시 커밋(React 가 이 렌더를 버리고 새 상태로 다시 그린다)
  if (stored.key !== key) setStored({ key, value: initial });
  const keyRef = useRef(key);
  keyRef.current = key;
  const value = stored.key === key ? stored.value : initial;
  const setValue = useCallback<Dispatch<SetStateAction<T>>>((next) => {
    setStored((prev) => {
      const current = prev.key === keyRef.current ? prev.value : initialRef.current;
      const resolved = typeof next === 'function' ? (next as (p: T) => T)(current) : next;
      return { key: keyRef.current, value: resolved };
    });
  }, []);
  return [value, setValue];
}
